import { ShieldPlusIcon } from '@phosphor-icons/react';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { getWorkspaceFormConfig, getWsConfigMemberColumns, WORKSPACE_MEMBER_FORM_SCHEMA } from '@constants';
import { FormListLayout } from '@containers';
import {
  ApiEndpoint,
  FormActionType,
  FormType,
  ServiceModalName,
  StatusType,
  Translation,
  WorkspaceMemberEndpoint,
} from '@enums';
import { useAppDispatch, useCreateModal, useFormHandlers } from '@hooks';
import { addServiceModal, deleteFormContent, removeServiceModal, submitFormRequest, updateFormProcedure } from '@store';
import i18n from '@translations';
import type { FormConfig, WorkspaceMember, WorkspaceMemberForm, WsConfigOwnershipProps } from '@types';

export const WsConfigMember: FC<WsConfigOwnershipProps> = ({ workspaceMember, isOwner = false }) => {
  const { id: application_id } = useParams();
  const dispatch = useAppDispatch();

  const formType = FormType.Workspace;
  const schema = WORKSPACE_MEMBER_FORM_SCHEMA;

  const { t: tCommon } = useTranslation(Translation.Common, { keyPrefix: 'button' });
  const { t: tModal } = useTranslation(Translation.Workspace, { keyPrefix: 'modals' });
  const { t: tDescription } = useTranslation(Translation.Workspace, { keyPrefix: 'descriptions' });
  const { t: tTabs } = useTranslation(Translation.Workspace, { keyPrefix: 'tabs' });

  const initialValues: WorkspaceMemberForm = useMemo(
    () => ({
      email: '',
    }),
    [workspaceMember, tCommon],
  );

  const handleSubmit = useCallback(
    (values: WorkspaceMemberForm) => {
      dispatch(
        submitFormRequest({
          formType,
          formData: values,
          params: {
            content_id: application_id,
          },
          globalOptions: {
            returnResult: true,
            content: true,
            endPath: true,
            returnPath: ApiEndpoint.CONFIGURATION,
            endpoint: WorkspaceMemberEndpoint.MEMBER,
          },
        }),
      );
    },
    [application_id, formType],
  );

  const formConfig: FormConfig = useMemo(
    () =>
      getWorkspaceFormConfig({
        endpoint: WorkspaceMemberEndpoint.MEMBER,
        schema,
      }),
    [schema, i18n.language],
  );

  const { openModal } = useCreateModal({
    formType,
    formConfig,
    initialValues,
    handleSubmit,
  });

  const handleStatus = (row: WorkspaceMember) => {
    dispatch(updateFormProcedure({ formType, procedure: FormActionType.Resend }));
    dispatch(
      addServiceModal({
        name: ServiceModalName.ConfirmModal,
        payload: {
          label: {
            title: `${tModal('resentInvitation')}?`,
            icon: <ShieldPlusIcon className="size-10 text-blue-400" />,
            text: tModal('aNewInvitationWillBeSentTo', { user: row?.name }),
            confirmButton: tCommon('invite'),
          },
          option: {
            modalType: StatusType.Info,
            modalTable: {
              formType,
            },
            modalForm: {
              preventDefault: {
                callback: true,
              },
            },
          },
          callbackFunction: {
            submit: () => {
              dispatch(removeServiceModal(ServiceModalName.ConfirmModal));
              dispatch(
                submitFormRequest({
                  formType,
                  formData: { email: row?.email },
                  params: {
                    content_id: application_id,
                  },
                  globalOptions: {
                    endPath: true,
                    returnPath: ApiEndpoint.CONFIGURATION,
                    endpoint: WorkspaceMemberEndpoint.RESEND_INVITATION,
                  },
                }),
              );
            },
          },
        },
      }),
    );
  };

  const handleCancel = () => {
    dispatch(updateFormProcedure({ formType, procedure: FormActionType.Idle }));
  };

  const memberListTable = useMemo(() => getWsConfigMemberColumns({ handleStatus, isOwner }), [tTabs, isOwner]);

  const workspaceMemberFormHandlers = useFormHandlers({
    formType,
    handlerOptions: [
      {
        type: FormActionType.Create,
        function: openModal,
        display: isOwner,
        label: tCommon('addMember'),
      },
      {
        type: FormActionType.Remove,
        display: isOwner,
        function: (rowData: WorkspaceMember) => {
          dispatch(
            addServiceModal({
              name: ServiceModalName.ConfirmModal,
              payload: {
                label: {
                  title: tModal('removeAccessTitle', { name: rowData.name }),
                  icon: <ShieldPlusIcon className="size-10 text-blue-400" />,
                  text: tDescription('confirmDeactivate', { name: rowData.name }),
                  confirmButton: tCommon('deactivate'),
                },
                option: {
                  modalType: StatusType.Delete,
                  modalTable: {
                    formType,
                  },
                },
                callbackFunction: {
                  submit: () => {
                    dispatch(
                      deleteFormContent({
                        formType,
                        formData: { email: rowData.email },
                        params: {
                          content_id: application_id,
                        },
                        globalOptions: {
                          returnResult: true,
                          content: true,
                          endPath: true,
                          returnPath: ApiEndpoint.CONFIGURATION,
                          endpoint: WorkspaceMemberEndpoint.MEMBER,
                        },
                      }),
                    );
                  },
                  cancel: handleCancel,
                },
              },
            }),
          );
        },
      },
    ],
  });

  return (
    <FormListLayout
      label={{
        title: tTabs('memberList'),
      }}
      tableData={{
        columns: memberListTable,
        actionHandlers: workspaceMemberFormHandlers,
        customTable: workspaceMember,
        formType,
      }}
    />
  );
};
